import { NavLink, Outlet, useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard,
  Users,
  CalendarCheck,
  FileBarChart,
  LogOut,
  ArrowLeftRight,
  Receipt,
  Wallet,
  UsersRound,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

/**
 * Combined "Team Room" layout — used for both /presently/* and /petty-cash/*
 * URL trees. The sidebar shows two grouped sections (Attendance + Petty Cash)
 * so a staff user can switch tasks without changing accounts.
 */

const HR_NAV = [
  { to: "/presently",             label: "Overview",   icon: LayoutDashboard, testid: "nav-overview", end: true },
  { to: "/presently/employees",   label: "Employees",  icon: Users,           testid: "nav-employees" },
  { to: "/presently/attendance",  label: "Attendance", icon: CalendarCheck,   testid: "nav-attendance" },
  { to: "/presently/reports",     label: "Reports",    icon: FileBarChart,    testid: "nav-reports" },
];

const PC_NAV = [
  { to: "/petty-cash",              label: "Overview",     icon: LayoutDashboard, testid: "pc-nav-overview", end: true },
  { to: "/petty-cash/transactions", label: "Transactions", icon: Receipt,         testid: "pc-nav-transactions" },
  { to: "/petty-cash/reports",      label: "Reports",      icon: FileBarChart,    testid: "pc-nav-reports" },
];

function NavItem({ to, label, Icon, testid, end, tone }) {
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={testid}
      className={({ isActive }) =>
        [
          "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm btn-lift font-medium",
          isActive
            ? "text-white shadow-soft"
            : "text-foreground/75 hover:bg-secondary hover:text-foreground",
        ].join(" ")
      }
      style={({ isActive }) => (isActive ? { background: tone } : undefined)}
    >
      <Icon className="h-4 w-4" />
      <span>{label}</span>
    </NavLink>
  );
}

export default function TeamRoomLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const initials = (user?.name || user?.email || "?")
    .split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  const HR_TONE = "hsl(var(--brand-teal))";
  const PC_TONE = "hsl(var(--status-halfday))";

  return (
    <div className="min-h-screen grain-bg relative">
      <div className="relative z-10 min-h-screen grid grid-cols-[240px_1fr]">
        {/* Sidebar */}
        <aside className="border-r border-border bg-card/60 backdrop-blur-md sticky top-0 h-screen flex flex-col">
          <Link
            to="/"
            className="px-6 py-6 border-b border-border block hover:bg-secondary/60 transition-colors"
            data-testid="brand-link"
          >
            <div className="text-[10px] uppercase tracking-[0.28em] text-muted-foreground font-mono-mini font-medium mb-2.5 whitespace-nowrap">
              SpaceAttire Teams
            </div>
            <div className="flex items-center gap-2.5">
              <div className="h-9 w-9 rounded-xl bg-brand text-white grid place-items-center shadow-soft">
                <UsersRound className="h-4 w-4" strokeWidth={2} />
              </div>
              <div>
                <div className="font-display text-lg font-bold leading-none">Team Room</div>
                <div className="eyebrow mt-1.5 whitespace-nowrap">HR · Attendance · Cash</div>
              </div>
            </div>
          </Link>

          <nav className="p-3 space-y-4 flex-1 overflow-y-auto" data-testid="team-sidebar-nav">
            {/* Attendance group */}
            <div>
              <div className="px-3 pb-1.5 text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-mono-mini font-medium flex items-center gap-1.5">
                <CalendarCheck className="h-3 w-3" />
                Attendance
              </div>
              <div className="space-y-1">
                {HR_NAV.map((item) => (
                  <NavItem
                    key={item.to}
                    to={item.to}
                    label={item.label}
                    Icon={item.icon}
                    testid={item.testid}
                    end={item.end}
                    tone={HR_TONE}
                  />
                ))}
              </div>
            </div>

            {/* Petty cash group */}
            <div>
              <div className="px-3 pb-1.5 text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-mono-mini font-medium flex items-center gap-1.5">
                <Wallet className="h-3 w-3" />
                Petty Cash
              </div>
              <div className="space-y-1">
                {PC_NAV.map((item) => (
                  <NavItem
                    key={item.to}
                    to={item.to}
                    label={item.label}
                    Icon={item.icon}
                    testid={item.testid}
                    end={item.end}
                    tone={PC_TONE}
                  />
                ))}
              </div>
            </div>
          </nav>

          <div className="p-3 border-t border-border">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="user-menu-trigger"
                  className="w-full flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-secondary transition-colors"
                >
                  <Avatar className="h-8 w-8 ring-2 ring-brand/20">
                    {user?.picture ? <AvatarImage src={user.picture} alt={user?.name} /> : null}
                    <AvatarFallback className="text-xs bg-brand text-white">{initials}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 text-left min-w-0">
                    <div className="text-sm font-medium truncate">{user?.name}</div>
                    <div className="text-[11px] text-muted-foreground truncate">{user?.email}</div>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" side="top" className="w-56">
                <DropdownMenuLabel className="text-xs uppercase tracking-wider text-muted-foreground">
                  Signed in as
                </DropdownMenuLabel>
                <DropdownMenuLabel className="pt-0 pb-2 truncate">{user?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild data-testid="back-to-hub-btn">
                  <Link to="/"><ArrowLeftRight className="h-4 w-4 mr-2" /> Module chooser</Link>
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="logout-btn"
                  onClick={async () => { await logout(); navigate("/workspace/login"); }}
                  className="text-destructive focus:text-destructive"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </aside>

        {/* Main */}
        <main className="min-h-screen">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
